import React, { useCallback, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { runInAction } from 'mobx';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faSlidersH, faReceipt, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import ExpenseNavigationBar from '../components/expense_configuration-navigation-bar';
import { fetch } from '../utils/api-wrapper';
import useLanguageChange from '../utils/hooks/useLanguageChange';
import { EXPENSE_FORM_FIELDS, normalizeExpenseFormLabels } from '../utils/expense-form-labels';

// Grouping is presentation only: the existing field IDs and save payload stay stable.
const LABEL_GROUPS = [
  { id: 'invoice', icon: faCalendarAlt },
  { id: 'classification', icon: faSlidersH },
  { id: 'details', icon: faReceipt },
  { id: 'travel', icon: faMapMarkerAlt },
];

/** XPEN-3654: empty inputs represent translated defaults, never persisted copies of them. */
export const ExpensesForm = ({ data }) => {
  useLanguageChange(data.t);
  const [draft, setDraft] = useState({});
  const [persistedLabels, setPersistedLabels] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Compare the actual save payload so trimming whitespace or reverting edits is not a change.
  const normalizedDraft = normalizeExpenseFormLabels(draft);
  const hasChanges = JSON.stringify(normalizedDraft) !== JSON.stringify(persistedLabels);

  const applyLabels = useCallback((rawLabels) => {
    const labels = normalizeExpenseFormLabels(rawLabels);
    // GET may contain another admin's latest changes. Keep expense forms and the
    // editor on the same labels, preserving all unrelated client configuration.
    runInAction(() => {
      const config = data.config || {};
      const expenses = config.expenses || {};
      data.config = { ...config, expenses: { ...expenses, form: { ...expenses.form, labels } } };
    });
    setDraft(labels);
    setPersistedLabels(labels);
  }, [data]);

  const load = useCallback(() => {
    setLoading(true);
    fetch('/expense_form/', 'GET', {}, (response) => {
      applyLabels(response.data.labels);
      setLoaded(true);
      setLoading(false);
    }, () => {
      toast.error(data.t.read('expenses_form.load_error'));
      setLoading(false);
    });
  }, [data.t, applyLabels]);
  useEffect(() => { load(); }, [load]);

  const save = (event) => {
    event.preventDefault();
    if (!loaded || loading || saving || !hasChanges) return;
    setSaving(true);
    fetch('/expense_form/', 'PUT', { labels: normalizedDraft }, (response) => {
      applyLabels(response.data.labels);
      setSaving(false);
      toast.success(data.t.read('expenses_form.saved'));
    }, () => {
      toast.error(data.t.read('expenses_form.save_error'));
      setSaving(false);
    });
  };

  return (
    <div className='card pl-4 pr-4 pb-3 rounded-lg'>
      <ExpenseNavigationBar data={data} page='form' />
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <div>
          <h4 className='mb-1'>{data.t.read('expenses_form.title')}</h4>
          <p className='small text-muted mb-0'>{data.t.read('expenses_form.description')}</p>
        </div>
        <button type='submit' form='expense-form-labels' className='btn btn-primary btn-sm ml-2' disabled={!loaded || loading || saving || !hasChanges}>
          {data.t.read('expenses_form.save')}
        </button>
      </div>
      {!loaded && !loading && <button type='button' className='btn btn-secondary mb-3' onClick={load}>{data.t.read('expenses_form.retry')}</button>}
      {loading && <p role='status'>{data.t.read('expenses_form.loading')}</p>}
      <form id='expense-form-labels' className='expense-configuration-scroll expense-label-editor mb-2' onSubmit={save}>
        <fieldset disabled={!loaded || loading || saving}>
          <legend className='sr-only'>{data.t.read('expenses_form.title')}</legend>
          <div className='expense-label-editor__columns'>
            <span>{data.t.read('expenses_form.default_label')}</span>
            <div>
              <span>{data.t.read('expenses_form.custom_label')}</span>
              <span className='expense-label-editor__hint'>{data.t.read('expenses_form.optional_hint')}</span>
            </div>
          </div>
          {LABEL_GROUPS.map((group) => {
            const fields = EXPENSE_FORM_FIELDS.filter((field) => field.group === group.id);
            const headingId = `expense-label-group-${group.id}`;
            return (
              <section className='expense-label-editor__group' key={group.id} aria-labelledby={headingId}>
                <div className='expense-label-editor__group-heading'>
                  <span className='expense-label-editor__icon' aria-hidden='true'><FontAwesomeIcon icon={group.icon} /></span>
                  <div className='expense-label-editor__group-copy'>
                    <h3 id={headingId}>{data.t.read(`expenses_form.groups.${group.id}.title`)}</h3>
                    <p>{data.t.read(`expenses_form.groups.${group.id}.description`)}</p>
                  </div>
                  <span className='expense-label-editor__count'>{data.t.read('expenses_form.label_count').replace('{count}', fields.length)}</span>
                </div>
                <div className='expense-label-editor__fields'>
                  {fields.map(({ id, translationKey }) => (
                    <div className='expense-label-editor__row' key={id}>
                      <label htmlFor={`expense-label-${id}`}>{data.t.read(translationKey)}</label>
                      <input
                        id={`expense-label-${id}`}
                        className='form-control'
                        type='text'
                        maxLength={120}
                        value={draft[id] || ''}
                        placeholder={data.t.read(translationKey)}
                        onChange={(event) => {
                          setDraft({ ...draft, [id]: event.target.value });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </fieldset>
      </form>
    </div>
  );
};

export default inject('data')(observer(ExpensesForm));
